import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

// Added resetKey prop
function Timer({ duration, onTimeUp, isRunning = true, resetKey }) {
    const { t } = useTranslation();
    const [displayTime, setDisplayTime] = useState(duration);
    const startTimeRef = useRef(null);
    const lastUpdateRef = useRef(0);
    const animationFrameRef = useRef(null);

    // Reset when duration changes OR when resetKey changes (e.g. score increases)
    useEffect(() => {
        setDisplayTime(duration);
        startTimeRef.current = performance.now();
        lastUpdateRef.current = 0;
    }, [duration, resetKey]); // Added resetKey dependency

    // Reset start time when resuming from a paused state
    useEffect(() => {
        if (isRunning) {
            // Adjust start time to preserve the remaining displayTime
            startTimeRef.current = performance.now() - (duration - displayTime) * 1000;
        }
    }, [isRunning, displayTime, duration]);

    // Animation frame loop with throttled state updates
    useEffect(() => {
        if (!isRunning) {
            return;
        }

        const updateDisplay = (currentTime) => {
            // Safety check for null start time
            if (startTimeRef.current === null) startTimeRef.current = currentTime;

            const elapsed = (currentTime - startTimeRef.current) / 1000;
            const remaining = Math.max(0, duration - elapsed);

            // Only update state every 100ms (10 times per second)
            if (currentTime - lastUpdateRef.current >= 100 || remaining <= 0) {
                lastUpdateRef.current = currentTime;
                setDisplayTime(remaining);

                if (remaining <= 0) {
                    if (onTimeUp) {
                        onTimeUp();
                    }
                    return; // Stop the animation loop
                }
            }

            animationFrameRef.current = requestAnimationFrame(updateDisplay);
        };

        animationFrameRef.current = requestAnimationFrame(updateDisplay);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [duration, isRunning, onTimeUp, resetKey]); // Added resetKey to ensure effect is fresh

    return (
        <div className="stat-box">
            <div className="stat-label">{t('time')}</div>
            <div className="stat-value">{displayTime.toFixed(1)}</div>
        </div>
    );
}

export default Timer;